import { useEffect, useState } from 'react';
import { gql } from 'graphql-request';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gqlClient } from '@/api';
import { localeLabels, type Locale, type Platform } from '@sonasi/shared-types';
import { rememberWebsiteUrl, useShareImage } from './share-image';
import { discardUpload, uploadFile } from './uploads';

export type AppStatusFilter = 'PUBLISHED' | 'DRAFT' | 'DISCONTINUED';

export interface LocaleProgress {
  locale: string;
  translated: number;
  total: number;
}

/** One page of the Apps table, with the count the pager needs. */
export interface AdminAppList {
  items: AdminAppRow[];
  /** Every app matching the filters, not the size of this page. */
  total: number;
  /** How many of those are live — counted over every page, not this one. */
  published: number;
}

export interface AdminAppRow {
  id: string;
  discontinuedAt: string | null;
  slug: string;
  name: string;
  platforms: string[];
  price: string | null;
  published: boolean;
  /** Where the app's icon is, or null while it has none. */
  iconUrl: string | null;
  /** Null on an app not yet linked to the App Store. */
  appleId: string | null;
  locales: LocaleProgress[];
  updatedAt: string;
}

/** Rows on one page of the table. See `useAdminApps` for why it is a page. */
export const APPS_PER_PAGE = 50;

const ADMIN_APPS = gql`
  query AdminApps($status: AppStatusFilter, $search: String, $limit: Int, $offset: Int) {
    adminApps(status: $status, search: $search, limit: $limit, offset: $offset) {
      items {
        id
        slug
        name
        platforms
        price
        published
        iconUrl
        appleId
        discontinuedAt
        locales {
          locale
          translated
          total
        }
        updatedAt
      }
      total
      published
    }
  }
`;

/**
 * How long to wait after the last keystroke before asking the server.
 *
 * Not a nicety. Without it every character is a request, and the rate limit is
 * five in the window — so typing "sketch" sent six queries and the sixth came
 * back "Too many requests", leaving the table showing an error for a search
 * that was perfectly valid. A test firing one change event never sees this;
 * a person typing sees it every time.
 */
const SEARCH_DEBOUNCE_MS = 300;

/** Follows `value`, but only settles once it has stopped changing. */
export const useDebounced = <T>(value: T, delay = SEARCH_DEBOUNCE_MS): T => {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
};

export interface AppPlanInput {
  /** Absent for a plan being added. */
  id?: string;
  name: string;
  price: string;
  period: 'MONTH' | 'YEAR' | 'ONCE';
}

const UPDATE_APP_PLANS = gql`
  mutation UpdateAppPlans($id: ID!, $plans: [AppPlanInput!]!) {
    updateAppPlans(id: $id, plans: $plans) {
      id
      plans {
        id
        name
        price
        period
        description {
          en
          vi
          ja
          ko
          zh
        }
      }
      locales {
        locale
        translated
        total
      }
      updatedAt
    }
  }
`;

export const useAdminApps = (
  filters: { status?: AppStatusFilter; search?: string; page?: number } = {},
) =>
  useQuery({
    // The filters are in the key, so switching them refetches rather than
    // showing the previous filter's rows under the new one's heading. The page
    // is in it for the same reason, and so going back to a page already seen
    // shows it instantly.
    queryKey: ['adminApps', filters.status ?? null, filters.search ?? '', filters.page ?? 1],
    queryFn: async () => {
      const data = await gqlClient.request<{ adminApps: AdminAppList }>(ADMIN_APPS, {
        status: filters.status ?? null,
        search: filters.search?.trim() || null,
        limit: APPS_PER_PAGE,
        offset: ((filters.page ?? 1) - 1) * APPS_PER_PAGE,
      });
      return data.adminApps;
    },
    /*
     * Keep the rows on screen while the next search runs. Otherwise every
     * refined search empties the table and replaces it with "Loading", so the
     * thing being narrowed down disappears exactly while somebody is looking
     * at it.
     */
    placeholderData: keepPreviousData,
  });

/**
 * The App Store's price tiers, as the picker offers them.
 *
 * **Apple's list, not Sonasi's** — the same kind of thing as the platform names
 * below and the set of locales: fixed elsewhere, rarely changed, and no part of
 * what the studio decides. It lived in the design fixture until now, among the
 * invented dashboard numbers and the placeholder blog posts, where a real
 * constant is indistinguishable from a mock-up's filler.
 *
 * ⚠️ **The picker is a convenience, not a rule.** `price` is a `varchar` and the
 * API accepts any string, so an app can hold a price that is not on this list —
 * which is why the editor keeps whatever an app already has at the top of its
 * own picker rather than silently dropping it. Enforcing the list here while
 * the server does not would only mean the screen refuses to show a value the
 * database is perfectly happy to hold.
 *
 * `Free` is the picker's word for no price at all; what reaches the API is null.
 */
export const PRICE_POINTS = [
  'Free',
  '$0.99',
  '$1.99',
  '$2.99',
  '$3.99',
  '$4.99',
  '$5.99',
  '$6.99',
  '$7.99',
  '$8.99',
  '$9.99',
  '$11.99',
  '$12.99',
  '$14.99',
  '$17.99',
  '$19.99',
  '$24.99',
  '$29.99',
  '$34.99',
  '$39.99',
  '$44.99',
  '$49.99',
  '$59.99',
  '$69.99',
  '$79.99',
  '$89.99',
  '$99.99',
  '$149.99',
  '$199.99',
] as const;

/**
 * GraphQL enum name to the name Apple uses.
 *
 * The database stores "macOS" and GraphQL sends `MACOS`, because an enum
 * travels as its member name and not its value. Joining the wire values
 * straight into the cell reads "MACOS · IPHONE · IPAD" — which looks like a
 * stylesheet doing it, so it survives a glance at the screen.
 */
/*
 * ⚠️ Keyed by `Uppercase<Platform>` rather than by `string`, so the compiler
 * carries the guard: adding a platform makes this record incomplete and the
 * file stops building until it has a label. Sooner and louder than a test.
 */
const PLATFORM_LABELS: Record<Uppercase<Platform>, string> = {
  MACOS: 'macOS',
  IPHONE: 'iPhone',
  IPAD: 'iPad',
};

/** The Platforms cell: "macOS · iPhone · iPad", the separator the design uses. */
export const platformLabel = (platforms: string[]): string =>
  platforms.length
    ? // An unmapped value is shown as it arrived rather than dropped: a platform
      // added to the API and forgotten here should look wrong, not look absent.
      platforms
        .map((platform) => (PLATFORM_LABELS as Record<string, string>)[platform] ?? platform)
        .join(' · ')
    : '—';

/**
 * The Price cell.
 *
 * A null price means the download is free, which is not the same as a price
 * nobody has filled in — the entity says so, and rendering it as a blank would
 * make a deliberate decision look like missing data.
 */
export const priceLabel = (price: string | null): string => price ?? 'Free';

/**
 * The Languages cell: the codes with something written in them.
 *
 * Locales are shown when they have any content rather than only when they are
 * complete, because the column answers "which languages does this app speak"
 * — and a half-translated app does speak that language. How far along each one
 * is belongs in the tooltip, where it does not turn one column into two.
 */
export const languageCodes = (locales: LocaleProgress[]): string[] =>
  locales.filter((locale) => locale.translated > 0).map((locale) => locale.locale.toUpperCase());

/** The detail behind the codes, for the cell's title attribute. */
export const languageDetail = (locales: LocaleProgress[]): string =>
  locales
    .map((locale) => `${locale.locale.toUpperCase()} ${locale.translated}/${locale.total}`)
    .join('\n');

export interface AppFeature {
  title: string;
  description: string;
  /**
   * Which drawing to show — a Lucide icon, by the name the library exports it
   * under (`AudioLines`) — or absent for a feature written before there was a
   * choice, or null for one somebody took the drawing off.
   *
   * The name is the library's, so the picker here, what the API validates
   * against and what the website draws are one list held in one place — each
   * app's own copy of Lucide, at the same version.
   */
  icon?: string | null;
}

export interface AppRequirement {
  key: string;
  value: string;
}

export interface AppFaqEntry {
  question: string;
  answer: string;
}

/**
 * One review, as the studio has chosen to quote it.
 *
 * Written here, never submitted from anywhere. `quote` and `by` rather than the
 * design file's `q` and `by`: the abbreviation saved four characters in a
 * mock-up and would have cost every reader of this type a trip to find out.
 */
export interface AppReview {
  quote: string;
  by: string;
}

export type LegalKind = 'TERMS' | 'PRIVACY';

export interface AppLegalDocument {
  effectiveOn: string;
  body: string;
}

/** The four sizes App Store Connect asks for. Named exactly as the API's enum. */
export type ScreenshotDevice = 'IPHONE_69' | 'IPHONE_65' | 'IPAD_13' | 'MAC';

/**
 * The lists "copy to other languages as untranslated" can queue a row in.
 *
 * Here rather than in the screen that draws the tick boxes, because this is
 * the file that puts them on the wire and the only one that can be wrong about
 * them. As `string[]` a typo compiled: `'featues'` reached the API, came back
 * as an enum it does not know, and threw the whole save away.
 *
 * Three, not the four the API accepts. `REQUIREMENTS` has no tick box because
 * requirements are edited as inline rows with no dialog to put one in;
 * `apps.contract.spec.tsx` pins that gap so a fifth block added to the API
 * cannot be quietly unreachable here.
 */
export const queueableBlocks = ['features', 'faq', 'reviews'] as const;
export type QueueableBlock = (typeof queueableBlocks)[number];

export interface AppScreenshot {
  id: string;
  device: ScreenshotDevice;
  position: number;
  alt: string | null;
  url: string;
}

export interface AdminApp {
  id: string;
  legal: Record<string, Record<string, AppLegalDocument>> | null;
  discontinuedAt: string | null;
  /** When it first went public, or null if it never has. Null means deletable. */
  publishedAt: string | null;
  slug: string;
  name: string;
  appStoreUrl: string | null;
  /** The address the website uses: `appStoreUrl`, or the one the Apple ID gives. */
  appStoreLink: string | null;
  appleId: string | null;
  bundleId: string | null;
  tagline: Partial<Record<Locale, string>> & { en: string };
  description: (Partial<Record<Locale, string[]>> & { en: string[] }) | null;
  platforms: string[];
  price: string | null;
  published: boolean;
  content: Record<string, unknown> | null;
  plans: {
    /** Stable, so a description written in five languages stays with its plan. */
    id: string;
    name: string;
    price: string;
    /*
     * The three the API's enum has, not `string`. GraphQL sends the member
     * name — `MONTH`, not `month` — and the editor's period picker needs to be
     * a closed list, so that a fourth one added on the server is a compile
     * error here rather than a row that renders blank.
     */
    period: AppPlanInput['period'];
    description?: Partial<Record<Locale, string>>;
  }[];
  /** The notes are one list per language now — the same bag as a plan's description. */
  releases: {
    id: string;
    version: string;
    releasedOn: string;
    items: Partial<Record<Locale, string[]>>;
  }[];
  /**
   * The App Store artwork, every device class at once.
   *
   * `url` is what the screen renders — never `fileId`. Where a file lives is a
   * deployment decision the API resolves, and a second way to build that
   * address is a second way to get it wrong.
   */
  screenshots: AppScreenshot[];
  /** Where the App Store icon is, or null while there is none. */
  iconUrl: string | null;
  /** The picture a link unfurls with, when one was chosen; null means the website draws it. */
  shareImageUrl: string | null;
  locales: LocaleProgress[];
  createdAt: string;
  updatedAt: string;
}

const ADMIN_APP = gql`
  query AdminApp($id: ID!) {
    adminApp(id: $id) {
      id
      slug
      name
      tagline {
        en
        vi
        ja
        ko
        zh
      }
      description {
        en
        vi
        ja
        ko
        zh
      }
      platforms
      price
      appStoreUrl
      appStoreLink
      appleId
      bundleId
      published
      content
      legal
      discontinuedAt
      publishedAt
      plans {
        id
        name
        price
        period
        description {
          en
          vi
          ja
          ko
          zh
        }
      }
      releases {
        id
        version
        releasedOn
        items {
          en
          ja
          ko
          zh
        }
      }
      screenshots {
        id
        device
        position
        alt
        url
      }
      iconUrl
      shareImageUrl
      locales {
        locale
        translated
        total
      }
      createdAt
      updatedAt
    }
    websiteUrl
  }
`;

export const useAdminApp = (id: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['adminApp', id],
    queryFn: async () => {
      const data = await gqlClient.request<{ adminApp: AdminApp | null; websiteUrl?: string }>(
        ADMIN_APP,
        { id },
      );
      rememberWebsiteUrl(queryClient, data.websiteUrl);
      return data.adminApp;
    },
    // An id that is not a uuid is answered null by the server rather than
    // thrown, so there is nothing here worth retrying.
    enabled: id.length > 0,
  });
};

/**
 * One language tab: the name in its own language, and how far it has got.
 *
 * The percentage is computed here rather than sent, because the server sends
 * the two numbers it is made of — "80%" alone cannot be checked, and the tab
 * needs the fraction anyway for its tooltip.
 */
export interface LanguageTab {
  locale: Locale;
  label: string;
  percent: number;
  translated: number;
  total: number;
}

export const languageTabs = (locales: LocaleProgress[]): LanguageTab[] =>
  locales.map((progress) => ({
    locale: progress.locale as Locale,
    label: localeLabels[progress.locale as Locale] ?? progress.locale,
    /*
     * Nothing to translate reads as complete, not as zero. An app whose English
     * is still empty is not 0% translated into Japanese — there is no work
     * outstanding, and showing 0% would send somebody looking for it.
     */
    percent: progress.total === 0 ? 100 : Math.round((progress.translated / progress.total) * 100),
    translated: progress.translated,
    total: progress.total,
  }));

/**
 * The half of an app that is the same in every language.
 *
 * Omitting a key leaves that field alone; sending null clears it. The screen
 * only ever sends the whole set, but the shape matters anyway: `price: null` is
 * how an app is marked free, and it has to survive the trip as null rather than
 * as an absent key.
 */
export interface UpdateAppInput {
  name?: string;
  slug?: string;
  platforms?: string[];
  price?: string | null;
  appStoreUrl?: string | null;
  appleId?: string;
  bundleId?: string | null;
  published?: boolean;
}

const UPDATE_APP = gql`
  mutation UpdateApp($id: ID!, $input: UpdateAppInput!) {
    updateApp(id: $id, input: $input) {
      id
      slug
      name
      tagline {
        en
        vi
        ja
        ko
        zh
      }
      description {
        en
        vi
        ja
        ko
        zh
      }
      platforms
      price
      appStoreUrl
      appStoreLink
      appleId
      bundleId
      published
      content
      plans {
        id
        name
        price
        period
      }
      releases {
        id
        version
        releasedOn
        items {
          en
          ja
          ko
          zh
        }
      }
      locales {
        locale
        translated
        total
      }
      createdAt
      updatedAt
    }
  }
`;

/**
 * What a failed save should say to the person who pressed Save.
 *
 * The server's message is used when it sent one, because the useful cases —
 * a slug already taken, a name left blank — are things only it can know. The
 * fallbacks cover the two ways there is no message to show.
 */
export const saveMessage = (error: unknown): string => {
  const response = (error as { response?: { errors?: { message?: string }[] } })?.response;
  const first = response?.errors?.[0]?.message;
  if (first) return first;
  if (error instanceof Error && error.message.includes('fetch')) {
    return 'Could not reach the server. Your changes have not been saved.';
  }
  return 'Could not save. Your changes have not been saved.';
};

export interface CreateAppInput {
  name: string;
  slug: string;
  tagline: { en: string };
  platforms?: string[];
  price?: string | null;
  /** Required: the number after "id" in the App Store link. */
  /** The App Store's number for the app, or null while it has none yet. */
  appleId: string | null;
  bundleId?: string | null;
}

const CREATE_APP = gql`
  mutation CreateApp($input: CreateAppInput!) {
    createApp(input: $input) {
      id
      slug
      name
    }
  }
`;

export const useCreateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAppInput) => {
      const data = await gqlClient.request<{
        createApp: { id: string; slug: string; name: string };
      }>(CREATE_APP, { input });
      return data.createApp;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['adminApps'] });
    },
  });
};

/**
 * The field a failure belongs to, when the server named one.
 *
 * A slug clash carries a code and a field so the message can sit under the box
 * that caused it. Matching on the English sentence instead would work until
 * somebody reworded it, and then the message would quietly move back to the top
 * of the form with nothing failing.
 */
export const errorField = (error: unknown): string | null => {
  /*
   * `extensions.field`, which is where the server puts it — checked against a
   * real response rather than guessed. The first version of this also looked
   * under `extensions.originalError.field`, a shape nothing ever sends; the
   * test passed because it asserted the same invention.
   */
  const extensions = (error as { response?: { errors?: { extensions?: { field?: string } }[] } })
    ?.response?.errors?.[0]?.extensions;
  return extensions?.field ?? null;
};

/**
 * A name turned into the slug it would get.
 *
 * Diacritics are stripped rather than dropped, so "Sổ tay" becomes "so-tay"
 * instead of "s-tay" — a Japanese name would otherwise lose most of itself.
 */
export const slugify = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Everything about an app written in one language.
 *
 * Omitting a key leaves that block alone in that language; an empty list says
 * this language has none. The editor sends the whole set, but the shape matters
 * anyway — the two mean different things and the server acts on the difference.
 */
export interface AppReleaseInput {
  id?: string;
  version: string;
  releasedOn: string;
  items: string[];
}

export interface AppContentInput {
  tagline?: string;
  description?: string[];
  /** App Store metadata, not website copy — nothing public renders these two. */
  promotionalText?: string;
  keywords?: string;
  /** What a search result and a share say instead of the name and the first paragraph; blank = automatic. */
  seoTitle?: string;
  seoDescription?: string;
  features?: AppFeature[];
  requirements?: AppRequirement[];
  faq?: AppFaqEntry[];
  reviews?: AppReview[];
  /** Addressed by plan id, so a partial list in any order is fine. */
  planDescriptions?: { planId: string; description: string }[];
}

const COPY_STUDIO_LEGAL = gql`
  mutation CopyStudioLegalToApp($id: ID!, $kind: LegalKind!, $locale: ContentLocale!) {
    copyStudioLegalToApp(id: $id, kind: $kind, locale: $locale) {
      copiedFrom
      app {
        id
        legal
        updatedAt
      }
    }
  }
`;

/**
 * Starts an app's own legal document from the studio-wide one.
 *
 * Its own mutation rather than part of Save, because it is not an edit: it
 * fetches text the person has not seen yet and puts it in front of them. Making
 * them press Save afterwards to find out what arrived would be asking them to
 * approve something invisible.
 */
export const useCopyStudioLegal = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kind, locale }: { kind: LegalKind; locale: Locale }) => {
      const data = await gqlClient.request<{
        copyStudioLegalToApp: { copiedFrom: string; app: AdminApp };
      }>(COPY_STUDIO_LEGAL, { id, kind, locale: locale.toUpperCase() });
      return data.copyStudioLegalToApp;
    },
    onSuccess: ({ app }) => {
      queryClient.setQueryData(['adminApp', id], (previous: AdminApp | undefined) =>
        previous ? { ...previous, ...app } : previous,
      );
    },
  });
};

const UPDATE_APP_LEGAL = gql`
  mutation UpdateAppLegal(
    $id: ID!
    $kind: LegalKind!
    $locale: ContentLocale!
    $document: AppLegalInput
  ) {
    updateAppLegal(id: $id, kind: $kind, locale: $locale, document: $document) {
      id
      legal
      updatedAt
    }
  }
`;

const UPDATE_APP_RELEASES = gql`
  mutation UpdateAppReleases($id: ID!, $locale: ContentLocale!, $releases: [AppReleaseInput!]!) {
    updateAppReleases(id: $id, locale: $locale, releases: $releases) {
      id
      releases {
        id
        version
        releasedOn
        items {
          en
          ja
          ko
          zh
        }
      }
      # The notes count towards each language now, so the figures come back
      # with them — a save that reloads the list but not the percentage left
      # the tab a step behind until a refresh.
      locales {
        locale
        translated
        total
      }
      updatedAt
    }
  }
`;

const UPDATE_APP_CONTENT = gql`
  mutation UpdateAppContent(
    $id: ID!
    $locale: ContentLocale!
    $content: AppContentInput!
    $queue: QueueUntranslatedInput
  ) {
    updateAppContent(id: $id, locale: $locale, content: $content, queue: $queue) {
      id
      tagline {
        en
        vi
        ja
        ko
        zh
      }
      description {
        en
        vi
        ja
        ko
        zh
      }
      content
      plans {
        id
        name
        price
        period
        description {
          en
          vi
          ja
          ko
          zh
        }
      }
      locales {
        locale
        translated
        total
      }
      updatedAt
    }
  }
`;

/**
 * A locale-keyed block out of the free-form `content` document.
 *
 * Read defensively because nothing in the schema enforces the shape: a stray
 * string or null there must not take down the editor for one malformed row.
 *
 * One check, not three. The first version also rejected a block that was a
 * string or an array before indexing it — branches the mutation table showed
 * could not be reached, because `'a string'['en']` and `[]['en']` are both
 * undefined and fall out of the same test below. A guard no test can drive is a
 * guard nobody can trust.
 */
export const contentBlock = <T>(
  content: Record<string, unknown> | null | undefined,
  block: string,
  locale: Locale,
): T[] => {
  const rows = (content?.[block] as Record<string, unknown> | undefined)?.[locale];
  return Array.isArray(rows) ? (rows as T[]) : [];
};

/**
 * The same read for a block holding one string per locale rather than a list.
 *
 * `contentBlock` returns `[]` for anything that is not an array, which for a
 * string block means every value it was asked for comes back empty — the field
 * would render blank however much was written in it.
 */
export const contentText = (
  content: Record<string, unknown> | null | undefined,
  block: string,
  locale: Locale,
): string => {
  const value = (content?.[block] as Record<string, unknown> | undefined)?.[locale];
  return typeof value === 'string' ? value : '';
};

/**
 * Saves the two halves of an app together.
 *
 * One Save button, two mutations, run in order: the language-independent half
 * first, then the language being edited. Sequential rather than in parallel
 * because both write the same row, and the second reply is the one the screen
 * keeps — a parallel pair would race and the loser's changes would appear to
 * vanish until a reload.
 */
export const useSaveApp = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      locale,
      basics,
      content,
      releases,
      plans,
      legal,
      queue,
    }: {
      locale: Locale;
      basics?: UpdateAppInput;
      content?: AppContentInput;
      /**
       * Where to leave a blank row waiting — the design's "copy to other
       * languages as untranslated".
       *
       * Sent with the content rather than on its own: the rows being queued are
       * the rows in that very request, and a second call would have to be told
       * about them again and could arrive when the first had failed.
       *
       * Blocks as well as languages, because the editor sends every list on
       * every content save — "queue what this request contains" would queue
       * four lists from a tick in one dialog.
       */
      queue?: { locales: Locale[]; blocks: QueueableBlock[] };
      releases?: AppReleaseInput[];
      plans?: AppPlanInput[];
      /*
       * One entry per document that changed. `document: null` clears the
       * override — the same thing as not calling the mutation for that
       * document at all, which is why nothing here has to mean "leave it".
       */
      legal?: { kind: LegalKind; document: AppLegalDocument | null }[];
    }) => {
      let app: AdminApp | null = null;
      if (basics) {
        app = (await gqlClient.request<{ updateApp: AdminApp }>(UPDATE_APP, { id, input: basics }))
          .updateApp;
      }
      if (releases) {
        // The notes in the list are this language's; the server merges them
        // into each release's bag and leaves the other languages alone.
        const saved = await gqlClient.request<{ updateAppReleases: AdminApp }>(
          UPDATE_APP_RELEASES,
          { id, locale: locale.toUpperCase(), releases },
        );
        app = { ...(app as AdminApp), ...saved.updateAppReleases };
      }
      /*
       * Before the content save, deliberately. A plan added in this same save
       * has no id until the server gives it one, so its description cannot be
       * addressed until this call has come back.
       */
      if (plans) {
        const saved = await gqlClient.request<{ updateAppPlans: AdminApp }>(UPDATE_APP_PLANS, {
          id,
          plans,
        });
        app = { ...(app as AdminApp), ...saved.updateAppPlans };
      }
      for (const entry of legal ?? []) {
        const saved = await gqlClient.request<{ updateAppLegal: AdminApp }>(UPDATE_APP_LEGAL, {
          id,
          kind: entry.kind,
          locale: locale.toUpperCase(),
          document: entry.document,
        });
        app = { ...(app as AdminApp), ...saved.updateAppLegal };
      }
      if (content) {
        const saved = await gqlClient.request<{ updateAppContent: AdminApp }>(UPDATE_APP_CONTENT, {
          id,
          locale: locale.toUpperCase(),
          content,
          /*
           * Both lists upper-cased, because both are GraphQL enums and an enum
           * travels by its *name*: `ContentLocale.VI` and `QueueableBlock.FEATURES`
           * are what the schema accepts, `'ja'` and `'features'` are only what
           * they mean once inside. Sending the value is rejected outright —
           * the whole save fails, content and all, on a variable the person
           * never typed.
           */
          queue: queue
            ? {
                locales: queue.locales.map((one) => one.toUpperCase()),
                blocks: queue.blocks.map((one) => one.toUpperCase()),
              }
            : undefined,
        });
        // Merged rather than replacing: the content reply is deliberately a
        // narrower selection than the one the editor renders.
        app = { ...(app as AdminApp), ...saved.updateAppContent };
      }
      return app as AdminApp;
    },
    onSuccess: (app) => {
      queryClient.setQueryData(['adminApp', id], (previous: AdminApp | undefined) =>
        previous ? { ...previous, ...app } : app,
      );
      void queryClient.invalidateQueries({ queryKey: ['adminApps'] });
    },
  });
};

/** The document an app has written for one kind and language, if any. */
export const legalDocument = (
  app: AdminApp | undefined,
  kind: LegalKind,
  locale: Locale,
): AppLegalDocument | null => {
  const forKind = app?.legal?.[kind.toLowerCase()];
  const document = forKind?.[locale];
  // Checked rather than trusted: the column is free-form JSON, and a row shaped
  // differently must not put an object where the editor expects text.
  return document && typeof document.body === 'string' ? document : null;
};

const DELETE_APP = gql`
  mutation DeleteApp($id: ID!) {
    deleteApp(id: $id)
  }
`;

/*
 * Its own mutation, not a flag on `deleteApp`. The two read almost the same in
 * a call and do opposite things — one keeps the page alive on purpose, the
 * other removes it — so the destructive one is named out loud.
 */
const PURGE_APP = gql`
  mutation PurgeApp($id: ID!) {
    purgeApp(id: $id)
  }
`;

const RESTORE_APP = gql`
  mutation RestoreApp($id: ID!) {
    restoreApp(id: $id) {
      id
      discontinuedAt
      updatedAt
    }
  }
`;

/**
 * Stops selling an app, and puts one back.
 *
 * Neither removes anything: an app that reached the App Store keeps getting
 * visitors from links nobody controls, so it leaves the catalogue and keeps its
 * own page.
 */
export const useDiscontinueApp = (id: string) => {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['adminApps'] });
    void queryClient.invalidateQueries({ queryKey: ['adminApp', id] });
  };

  return {
    stop: useMutation({
      mutationFn: async () => {
        const data = await gqlClient.request<{ deleteApp: boolean }>(DELETE_APP, { id });
        return data.deleteApp;
      },
      onSuccess: refresh,
    }),
    restore: useMutation({
      mutationFn: async () => {
        const data = await gqlClient.request<{ restoreApp: AdminApp }>(RESTORE_APP, { id });
        return data.restoreApp;
      },
      onSuccess: refresh,
    }),
    purge: useMutation({
      mutationFn: async () => {
        const data = await gqlClient.request<{ purgeApp: boolean }>(PURGE_APP, { id });
        return data.purgeApp;
      },
      /*
       * The app is gone, so its own cache entry is removed rather than marked
       * stale: invalidating would refetch an id the server no longer knows and
       * leave the error on screen after a successful delete.
       */
      onSuccess: () => {
        queryClient.removeQueries({ queryKey: ['adminApp', id] });
        void queryClient.invalidateQueries({ queryKey: ['adminApps'] });
      },
    }),
  };
};

/* ---------------------------------------------------------------------- */

const ADD_SCREENSHOT = gql`
  mutation AddAppScreenshot($id: ID!, $device: ScreenshotDevice!, $fileId: ID!, $alt: String) {
    addAppScreenshot(id: $id, device: $device, fileId: $fileId, alt: $alt) {
      id
      device
      position
      alt
      url
    }
  }
`;

const REMOVE_SCREENSHOT = gql`
  mutation RemoveAppScreenshot($id: ID!, $screenshotId: ID!) {
    removeAppScreenshot(id: $id, screenshotId: $screenshotId) {
      id
      device
      position
      alt
      url
    }
  }
`;

const SET_APP_ICON = gql`
  mutation SetAppIcon($id: ID!, $fileId: ID) {
    setAppIcon(id: $id, fileId: $fileId) {
      id
      iconUrl
      updatedAt
    }
  }
`;

const REORDER_SCREENSHOTS = gql`
  mutation ReorderAppScreenshots($id: ID!, $device: ScreenshotDevice!, $screenshotIds: [ID!]!) {
    reorderAppScreenshots(id: $id, device: $device, screenshotIds: $screenshotIds) {
      id
      device
      position
      alt
      url
    }
  }
`;

/**
 * Adding, removing and reordering an app's screenshots.
 *
 * Every one answers with the app's whole list, because every one moves the
 * positions of things it did not touch: removing the second of five renumbers
 * the three after it. A screen keeping its own copy would be showing an order
 * the database does not have.
 */
export const useAppScreenshots = (id: string) => {
  const queryClient = useQueryClient();
  const refresh = (screenshots: AppScreenshot[]) => {
    queryClient.setQueryData<AdminApp>(['adminApp', id], (current) =>
      current ? { ...current, screenshots } : current,
    );
  };

  return {
    add: useMutation({
      mutationFn: async ({ device, file }: { device: ScreenshotDevice; file: File }) => {
        // Two steps, in order: the bytes first, then the row that points at
        // them. A file with no screenshot is waste; a screenshot with no file
        // is a broken picture.
        const stored = await uploadFile(file);
        try {
          const data = await gqlClient.request<{ addAppScreenshot: AppScreenshot[] }>(
            ADD_SCREENSHOT,
            { id, device, fileId: stored.id, alt: null },
          );
          return data.addAppScreenshot;
        } catch (error) {
          // The bytes are already stored and now have nothing pointing at
          // them — a tenth screenshot refused, a session that expired between
          // the two calls, a dropped connection.
          await discardUpload(stored.id);
          throw error;
        }
      },
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: async (screenshotId: string) => {
        const data = await gqlClient.request<{ removeAppScreenshot: AppScreenshot[] }>(
          REMOVE_SCREENSHOT,
          { id, screenshotId },
        );
        return data.removeAppScreenshot;
      },
      onSuccess: refresh,
    }),
    /**
     * Sets the icon, or clears it when no file is given.
     *
     * One call for both, because the screen has one control: a box that either
     * holds a picture or is empty.
     */
    icon: useMutation({
      mutationFn: async (file: File | null) => {
        const stored = file ? await uploadFile(file) : null;
        try {
          const data = await gqlClient.request<{
            setAppIcon: { iconUrl: string | null; updatedAt: string };
          }>(SET_APP_ICON, { id, fileId: stored?.id ?? null });
          return data.setAppIcon;
        } catch (error) {
          // The icon is refused for its size more often than for anything
          // else, and the bytes are already stored when that happens.
          if (stored) await discardUpload(stored.id);
          throw error;
        }
      },
      onSuccess: ({ iconUrl, updatedAt }) => {
        // `updatedAt` too: the share preview is addressed by it, and the
        // website draws the icon into that picture.
        queryClient.setQueryData<AdminApp>(['adminApp', id], (current) =>
          current ? { ...current, iconUrl, updatedAt } : current,
        );
      },
    }),
    reorder: useMutation({
      mutationFn: async ({
        device,
        screenshotIds,
      }: {
        device: ScreenshotDevice;
        screenshotIds: string[];
      }) => {
        const data = await gqlClient.request<{ reorderAppScreenshots: AppScreenshot[] }>(
          REORDER_SCREENSHOTS,
          { id, device, screenshotIds },
        );
        return data.reorderAppScreenshots;
      },
      onSuccess: refresh,
    }),
  };
};

const SET_APP_SHARE_IMAGE = gql`
  mutation SetAppShareImage($id: ID!, $fileId: ID) {
    setAppShareImage(id: $id, fileId: $fileId) {
      id
      shareImageUrl
      updatedAt
    }
  }
`;

/** The picture a link to this app unfurls with, set and cleared like the icon. */
export const useAppShareImage = (id: string) =>
  useShareImage<AdminApp>({
    mutation: SET_APP_SHARE_IMAGE,
    variables: { id },
    queryKey: ['adminApp', id],
  });
